import { createFileRoute, Link } from "@tanstack/react-router";
import { hreflangLinks, articleMeta, absoluteUrl } from "@/lib/seo";
import { articleHeroFor } from "@/lib/article-heroes";
import { BlogPost, articleJsonLd, breadcrumbJsonLd } from "@/components/BlogPost";

const SLUG = "montessori-vaatehuolto-opas";
const URL = `https://montessori24.fi/montessori-koulu/${SLUG}`;
const TITLE = "Montessori-vaatehuolto — Itsenäisen pukeutumisen opas";
const DESC = "Miten järjestät lapsen vaaterekin ja vaatehuollon Montessori-tyyliin — itsenäinen pukeutuminen 1–6-vuotiaille. Ikäopas, tarkistuslista ja hyödyt kehitykselle.";
const PUBLISHED = "2026-07-17";
const HERO = articleHeroFor(SLUG);

export const Route = createFileRoute("/montessori-koulu/montessori-vaatehuolto-opas")({
  head: () => ({
    meta: articleMeta({
      slug: SLUG,
      title: TITLE,
      description: DESC,
      published: PUBLISHED,
      image: HERO,
    }),
    links: [{ rel: "canonical", href: URL }, ...hreflangLinks(URL)],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(articleJsonLd({ title: TITLE, description: DESC, url: URL, image: absoluteUrl(HERO), publishedISO: PUBLISHED })) },
      { type: "application/ld+json", children: JSON.stringify(breadcrumbJsonLd(SLUG, TITLE)) },
    ],
  }),
  component: Post,
});

function Post() {
  return (
    <BlogPost
      eyebrow="Itsenäinen pukeutuminen"
      title={TITLE}
      lead="Kun lapsi ulottuu vaatteisiinsa itse, hän oppii valitsemaan, päättämään ja pukeutumaan omin voimin. Vaaterekki on Montessori-kodin yksinkertaisin ja vaikuttavin muutos — tässä ikäopas 1–6-vuotiaille sekä käytännön tarkistuslista pukeutumispisteen rakentamiseen."
      readingMinutes={7}
      publishedISO={PUBLISHED}
    >
      <h2>Miksi lapsen korkuinen vaaterekki?</h2>
      <p>Montessori-pedagogiikan ytimessä on <strong>valmisteltu ympäristö</strong>: lapsi pystyy toimimaan itse, koska tavarat ovat hänen tasollaan. Aikuisen korkuinen komero viestii lapselle "tämä ei kuulu sinulle". Lapsen korkuinen <Link to="/lasten-vaaterekki">vaaterekki</Link> viestii päinvastaista — <em>sinä valitset, sinä päätät, sinä pukeudut</em>.</p>
      <p>Käytännön hyödyt näkyvät nopeasti:</p>
      <ul>
        <li><strong>Hienomotoriikka</strong> — henkarien pujottaminen ja nappien avaaminen ovat käden harjoitusta.</li>
        <li><strong>Päätöksenteko</strong> — kaksi paitaa rinnakkain on lapsen ensimmäinen päivittäinen valinta.</li>
        <li><strong>Aikakäsitys ja rutiini</strong> — sama paikka joka aamu rakentaa ennakoitavaa arkea.</li>
        <li><strong>Itsetunto</strong> — "minä osaan" toistuu joka päivä ilman aikuisen apua.</li>
      </ul>

      <h2>Ikäopas 1–6 vuotta</h2>
      <h3>1–2 vuotta — tutustumisvaihe</h3>
      <p>Taapero ei vielä pukeudu itse, mutta hän <strong>osallistuu</strong>. Aseta rekille 2–3 vaihtoehtoa, ei enempää. Anna lapsen ottaa henkari alas ja tuoda se sinulle. Nimeä värit ja vaatekappaleet ääneen.</p>
      <ul>
        <li>Matala rekki (n. 70–90 cm lattiasta).</li>
        <li>Kevyet muovi- tai puuhenkarit — ei metalliripustimia.</li>
        <li>Yksi pieni kori sukille ja alusvaatteille rekin alle.</li>
      </ul>

      <h3>2–3 vuotta — ensimmäiset omat valinnat</h3>
      <p>Lapsi osaa vetää housut jalkaan, avata tarranauhat ja valita paidan. Anna hänen valita edellisenä iltana <em>huomisen asu</em> ja ripustaa se erikseen — tämä yksi rutiini poistaa aamun kiireen kokonaan.</p>

      <h3>3–4 vuotta — itsenäinen pukeutuminen</h3>
      <p>Napit, vetoketjut ja henkarille ripustaminen alkavat sujua. Lisää rekin viereen <strong>peili</strong> ja pieni jakkara — lapsi tarkistaa itse, onko paita oikein päin. Älä korjaa väärin puettuja vaatteita heti; itsehavainnointi on osa oppimista.</p>

      <h3>4–6 vuotta — täysi itsenäisyys</h3>
      <p>Esikouluikäinen pukeutuu itse, mutta hyötyy edelleen valmiiksi lajitellusta vaatemäärästä. Vaihda kausivaatteet yhdessä joka 3 kuukausi — tämä on hyvä hetki opettaa <strong>vaatehuoltoa</strong>: taittelu, likapyykkiin vieminen ja hoito-ohjelappujen lukeminen.</p>

      <h2>Pukeutumispisteen tarkistuslista</h2>
      <ol>
        <li>Rekki on lapsen olkapään korkeudella — ei korkeammalla.</li>
        <li>Tanko on tukeva ja rekin jalusta ei kaadu, kun lapsi nojaa siihen.</li>
        <li>Vaatteita on 5–8 vaihtoehtoa, ei enempää — liika valinta kuormittaa.</li>
        <li>Vain kauden vaatteet — kesällä ei ole talvitakkia näkyvillä.</li>
        <li>Alahyllyllä tai korissa: sukat, alusvaatteet, sisätossut.</li>
        <li>Peili vieressä — täyspitkä tai lapsen kokoinen.</li>
        <li>Likapyykkikori samassa nurkassa — sulkee kierron.</li>
        <li>Aikuinen ei koskea rekkiin ilman lapsen kutsua.</li>
      </ol>

      <h2>Yleiset kompastuskivet</h2>
      <p><strong>"Lapsi valitsee aina saman paidan."</strong> Anna hänen. Toisto rakentaa turvaa. Jos hygienia huolestuttaa, pese paita yöllä ja palauta rekille aamuksi.</p>
      <p><strong>"Aamut ovat kaoottiset."</strong> Siirrä valinta iltaan. Lapsi valitsee huomisen asun ennen nukkumaanmenoa — aamu on vain pukemista.</p>
      <p><strong>"Vaatteet päätyvät lattialle."</strong> Odotettavaa 1–3-vuotiaana. Näytä henkarin käyttö ilman moitetta, joka päivä, kunnes se sujuu. Toistoa ei voi ohittaa.</p>

      <h2>Yhteenveto</h2>
      <p>Vaaterekki on Montessori-kodin <strong>halvin ja nopein muutos</strong>, jolla saat suurimman kehitysvaikutuksen. Aloita yhdellä matalalla rekillä, viidellä vaatteella ja peilillä — lapsi tekee lopun itse.</p>

      <p>Lue myös: <Link to="/montessori-koulu/montessori-kalusteet-kotiin">Montessori-kalusteet kotiin — mistä aloittaa</Link>. Tai siirry suoraan tuotteisiin: <Link to="/lasten-vaaterekki">Selaa lasten vaaterekkejä →</Link></p>
    </BlogPost>
  );
}
